import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  IconButton,
  List,
  ListItem,
  Select,
  MenuItem,
  Skeleton,
  Stack,
  Switch as ToggleSwitch,
  TextField,
  Typography,
  useTokens,
} from '@barracuda-internal/bds-core';
import { Add, Close, Delete, Edit } from '@barracuda-internal/bds-core/dist/Icons/Core';

import AADRoleGuard from 'common/components/AADRoleGuard';
import { actions } from './globalReducer';

const EMPTY_FORM = {
  id: '',
  name: '',
  region: '',
  status: 'active',
  token_threshold: '',
  window_overlap: '',
  quick_timeout_s: '',
  max_accumulated_mb: '',
};

const SCAN_CONFIG_FIELDS = [
  { key: 'token_threshold', label: 'Token threshold', placeholder: '500', help: 'Trigger quick scan after N tokens' },
  { key: 'window_overlap', label: 'Window overlap', placeholder: '100', help: 'Tokens to overlap between scans' },
  { key: 'quick_timeout_s', label: 'Quick timeout (s)', placeholder: '5', help: 'Quick scan timeout in seconds' },
  { key: 'max_accumulated_mb', label: 'Max accumulated (MB)', placeholder: '10', help: 'Max buffer size in MB' },
];

/**
 * Validates that a string represents a positive integer.
 * Returns { valid: true, value: number|null } or { valid: false, error: string }.
 */
const validatePositiveInteger = (value) => {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  if (trimmed === '' || value === null || value === undefined) {
    return { valid: true, value: null }; // Empty means "inherit"
  }

  const num = Number(trimmed);

  if (Number.isNaN(num)) {
    return { valid: false, error: 'Must be a valid number' };
  }

  if (!Number.isFinite(num)) {
    return { valid: false, error: 'Value is out of range' };
  }

  if (!Number.isInteger(num)) {
    return { valid: false, error: 'Must be a whole number (no decimals)' };
  }

  if (num < 0) {
    return { valid: false, error: 'Must be a positive number' };
  }

  return { valid: true, value: num };
};

const EMPTY_BACKEND_FORM = {
  backend_id: '',
  url: '',
  timeout: '30',
  api_token: '',
  enabled: true,
};

const GatewaysContainer = () => {
  const tokens = useTokens();
  const dispatch = useDispatch();
  const gateways = useSelector((state) => state.global.gateways);
  const gatewayBackends = useSelector((state) => state.global.gatewayBackends);
  const gatewayBackendForm = useSelector((state) => state.global.gatewayBackendForm);
  const backendDefinitions = useSelector((state) => state.global.backendDefinitions);
  const authRoles = useSelector((state) => state.auth?.roles || []);
  const isAdmin = authRoles.includes('ADMIN');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [drawerGateway, setDrawerGateway] = useState(null);

  // Backend config dialog state
  const [backendDialogOpen, setBackendDialogOpen] = useState(false);
  const [editingBackendConfig, setEditingBackendConfig] = useState(null);
  const [backendForm, setBackendForm] = useState(EMPTY_BACKEND_FORM);
  const [backendFormErrors, setBackendFormErrors] = useState({});
  const [deleteBackendTarget, setDeleteBackendTarget] = useState(null);

  useEffect(() => {
    dispatch(actions.fetchGateways());
    dispatch(actions.fetchBackendDefinitions());
  }, [dispatch]);

  // Fetch backend configs when drawer opens
  useEffect(() => {
    if (drawerGateway) {
      dispatch(actions.fetchGatewayBackends(drawerGateway.id));
    }
  }, [dispatch, drawerGateway]);

  const items = gateways?.items || [];
  const isLoading = gateways?.isLoading;
  const error = gateways?.error;

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingGateway(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
  };

  const openCreateDialog = () => {
    setEditingGateway(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEditDialog = (gw) => {
    setEditingGateway(gw);
    setForm({
      id: gw.id || '',
      name: gw.name || '',
      region: gw.region || '',
      status: gw.status || 'active',
      token_threshold: gw.token_threshold?.toString() ?? '',
      window_overlap: gw.window_overlap?.toString() ?? '',
      quick_timeout_s: gw.quick_timeout_s?.toString() ?? '',
      max_accumulated_mb: gw.max_accumulated_mb?.toString() ?? '',
    });
    setDialogOpen(true);
  };

  const submit = () => {
    // Validate scan config fields
    const newErrors = {};
    const payload = {
      name: form.name.trim(),
      region: form.region.trim(),
      status: form.status,
    };

    SCAN_CONFIG_FIELDS.forEach(({ key }) => {
      const result = validatePositiveInteger(form[key]);
      if (!result.valid) {
        newErrors[key] = result.error;
      } else {
        payload[key] = result.value;
      }
    });

    // If any validation errors, show them and don't save
    if (Object.keys(newErrors).length > 0) {
      setFormErrors(newErrors);
      return;
    }

    if (editingGateway) {
      dispatch(actions.updateGateway(editingGateway.id, payload));
    } else {
      payload.id = form.id.trim();
      dispatch(actions.createGateway(payload));
    }
    closeDialog();
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      dispatch(actions.deleteGateway(deleteTarget.id));
      setDeleteTarget(null);
    }
  };

  // Backend config handlers
  const closeBackendDialog = () => {
    setBackendDialogOpen(false);
    setEditingBackendConfig(null);
    setBackendForm(EMPTY_BACKEND_FORM);
    setBackendFormErrors({});
    dispatch(actions.clearGatewayBackendForm());
  };

  const openCreateBackendDialog = () => {
    setEditingBackendConfig(null);
    setBackendForm(EMPTY_BACKEND_FORM);
    setBackendDialogOpen(true);
  };

  const openEditBackendDialog = (config) => {
    setEditingBackendConfig(config);
    setBackendForm({
      backend_id: config.backend_id?.toString() || '',
      url: config.url || '',
      timeout: config.timeout?.toString() || '30',
      api_token: '',
      enabled: config.enabled ?? true,
    });
    setBackendDialogOpen(true);
  };

  const submitBackendForm = () => {
    const newErrors = {};
    if (!editingBackendConfig && !backendForm.backend_id) {
      newErrors.backend_id = 'Backend is required';
    }
    if (!backendForm.url) {
      newErrors.url = 'URL is required';
    }
    const timeoutVal = parseFloat(backendForm.timeout);
    if (Number.isNaN(timeoutVal) || timeoutVal <= 0) {
      newErrors.timeout = 'Must be a positive number';
    }

    if (Object.keys(newErrors).length > 0) {
      setBackendFormErrors(newErrors);
      return;
    }

    const payload = {
      url: backendForm.url.trim(),
      timeout: timeoutVal,
      enabled: backendForm.enabled,
    };
    if (backendForm.api_token) {
      payload.api_token = backendForm.api_token;
    }

    if (editingBackendConfig) {
      dispatch(actions.updateGatewayBackend(drawerGateway.id, editingBackendConfig.id, payload));
    } else {
      payload.backend_id = parseInt(backendForm.backend_id, 10);
      dispatch(actions.createGatewayBackend(drawerGateway.id, payload));
    }
    closeBackendDialog();
  };

  const confirmDeleteBackend = () => {
    if (deleteBackendTarget && drawerGateway) {
      dispatch(actions.deleteGatewayBackend(drawerGateway.id, deleteBackendTarget.id));
      setDeleteBackendTarget(null);
    }
  };

  // Get available backends (not already configured for this gateway)
  const configuredBackendIds = new Set(gatewayBackends.items.map((c) => c.backend_id));
  const availableBackends = (backendDefinitions.items || []).filter(
    (b) => b.enabled && !configuredBackendIds.has(b.id)
  );

  const isCreate = !editingGateway;
  const canSubmit =
    (isCreate ? form.id.trim() : true) && form.name.trim() && form.region.trim();

  return (
    <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
      <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: tokens.aliasesSpaceComponentDefault,
          }}
        >
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDense }}>
            <Typography variant="h2">Gateways</Typography>
            <Typography variant="captionSubtle" sx={{ maxWidth: 600 }}>
              Registered gateway instances. Each gateway can have its own scan
              configuration which overrides code defaults. Per-account config
              overrides gateway config.
            </Typography>
          </Stack>
          <AADRoleGuard requires="ADMIN">
            <Button
              bdsType="interactiveEmphasis"
              startIcon={<Add />}
              onClick={openCreateDialog}
            >
              Add gateway
            </Button>
          </AADRoleGuard>
        </Stack>

        <Card bdsType="staticSubtle" bdsBoxShadow="default" bdsPadding="spacious">
          <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
            <Typography variant="h4">Registered gateways</Typography>

            {isLoading ? (
              <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
                <Skeleton variant="rectangular" width="100%" height={80} />
                <Skeleton variant="rectangular" width="100%" height={80} />
              </Stack>
            ) : error ? (
              <Typography variant="body1" sx={{ color: tokens.aliasesColorTextError }}>
                {error}
              </Typography>
            ) : items.length === 0 ? (
              <Box
                sx={{
                  padding: tokens.aliasesSpaceContainerSpacious,
                  textAlign: 'center',
                  border: `1px dashed ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                  borderRadius: tokens.aliasesBorderRadiusDefault,
                }}
              >
                <Typography variant="body1">No gateways registered</Typography>
                <Typography variant="captionSubtle">
                  Gateways register themselves on startup, or you can add one manually.
                </Typography>
              </Box>
            ) : (
              <List>
                {items.map((gw) => (
                  <ListItem
                    key={gw.id}
                    bdsType="interactiveSubtle"
                    padding="default"
                    onClick={() => setDrawerGateway(gw)}
                    sx={{
                      cursor: 'pointer',
                      borderBottom: `1px solid ${tokens.aliasesColorBorderStaticContainerSubtle}`,
                    }}
                  >
                    <Stack
                      direction="row"
                      sx={{
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        gap: tokens.aliasesSpaceComponentDefault,
                      }}
                    >
                      <Stack sx={{ gap: tokens.aliasesSpaceComponentDense, flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          sx={{ gap: tokens.aliasesSpaceComponentDense, alignItems: 'center' }}
                        >
                          <Typography variant="body1Bold">{gw.name}</Typography>
                          <Chip
                            label={gw.status === 'active' ? 'Active' : 'Inactive'}
                            bdsType={gw.status === 'active' ? 'staticSuccess' : 'staticNeutral'}
                            bdsSize="medium"
                          />
                        </Stack>
                        <Typography variant="captionSubtle" sx={{ fontFamily: 'monospace' }}>
                          {gw.id}
                        </Typography>
                        <Typography variant="captionSubtle">
                          Region: {gw.region}
                          {gw.token_threshold && ` · Threshold: ${gw.token_threshold}`}
                        </Typography>
                      </Stack>
                      <AADRoleGuard requires="ADMIN">
                        <Stack
                          direction="row"
                          sx={{ gap: tokens.aliasesSpaceComponentDense }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <IconButton
                            bdsType="interactiveSubtle"
                            size="small"
                            onClick={() => openEditDialog(gw)}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            bdsType="interactiveSubtle"
                            size="small"
                            onClick={() => setDeleteTarget(gw)}
                          >
                            <Delete />
                          </IconButton>
                        </Stack>
                      </AADRoleGuard>
                    </Stack>
                  </ListItem>
                ))}
              </List>
            )}
          </Stack>
        </Card>
      </Stack>

      {/* Create / Edit dialog */}
      <Dialog open={dialogOpen} onClose={closeDialog} bdsDialogWidth="medium">
        <DialogTitle
          dialogTitle={editingGateway ? `Edit ${editingGateway.name}` : 'Add gateway'}
          showCloseIcon
          onClose={closeDialog}
        />
        <DialogContent>
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault, marginTop: tokens.aliasesSpaceComponentDense }}>
            <TextField
              label="Gateway ID"
              value={form.id}
              onChange={(e) => setForm({ ...form, id: e.target.value })}
              disabled={!!editingGateway}
              helperText={editingGateway ? 'ID cannot be changed' : 'Unique identifier (e.g. gw-us-east-1)'}
              fullWidth
              bdsSize="small"
            />
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              fullWidth
              bdsSize="small"
              placeholder="US East Gateway"
            />
            <TextField
              label="Region"
              value={form.region}
              onChange={(e) => setForm({ ...form, region: e.target.value })}
              fullWidth
              bdsSize="small"
              placeholder="us-east-1"
            />
            <Stack direction="row" sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDefault }}>
              <Typography variant="body1" sx={{ flex: 1 }}>Active</Typography>
              <ToggleSwitch
                checked={form.status === 'active'}
                onChange={(_, checked) => setForm({ ...form, status: checked ? 'active' : 'inactive' })}
              />
            </Stack>

            <Typography variant="body1Bold" sx={{ marginTop: tokens.aliasesSpaceComponentDefault }}>
              Scan configuration (optional overrides)
            </Typography>
            <Typography variant="captionSubtle">
              Leave blank to use code defaults. These are overridden by per-account settings.
            </Typography>

            {SCAN_CONFIG_FIELDS.map(({ key, label, placeholder, help }) => (
              <TextField
                key={key}
                label={label}
                value={form[key]}
                onChange={(e) => {
                  setForm({ ...form, [key]: e.target.value });
                  // Clear error when user types
                  if (formErrors[key]) {
                    setFormErrors((prev) => ({ ...prev, [key]: undefined }));
                  }
                }}
                fullWidth
                bdsSize="small"
                placeholder={placeholder}
                error={Boolean(formErrors[key])}
                helperText={formErrors[key] || help}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={closeDialog}>Cancel</Button>
          <Button bdsType="interactiveEmphasis" onClick={submit} disabled={!canSubmit}>
            {editingGateway ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        bdsDialogWidth="small"
      >
        <DialogTitle
          dialogTitle={`Delete ${deleteTarget?.name || 'gateway'}`}
          showCloseIcon
          onClose={() => setDeleteTarget(null)}
        />
        <DialogContent
          content={
            deleteTarget
              ? `This will permanently delete the gateway "${deleteTarget.name}" (${deleteTarget.id}). This action cannot be undone.`
              : ''
          }
        />
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button bdsType="interactiveEmphasis" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerGateway)}
        onClose={() => setDrawerGateway(null)}
        PaperProps={{ sx: { width: 420 } }}
      >
        {drawerGateway && (
          <Box sx={{ padding: tokens.aliasesSpaceContainerSpacious }}>
            <Stack
              direction="row"
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: tokens.aliasesSpaceContainerDefault,
              }}
            >
              <Typography variant="h3">Gateway details</Typography>
              <IconButton bdsType="interactiveSubtle" onClick={() => setDrawerGateway(null)}>
                <Close />
              </IconButton>
            </Stack>

            <Stack sx={{ gap: tokens.aliasesSpaceContainerDefault }}>
              <Box>
                <Typography variant="captionSubtle">ID</Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                  {drawerGateway.id}
                </Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">Name</Typography>
                <Typography variant="body1">{drawerGateway.name}</Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">Region</Typography>
                <Typography variant="body1">{drawerGateway.region}</Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">Status</Typography>
                <Chip
                  label={drawerGateway.status === 'active' ? 'Active' : 'Inactive'}
                  bdsType={drawerGateway.status === 'active' ? 'staticSuccess' : 'staticNeutral'}
                  bdsSize="medium"
                />
              </Box>

              <Typography variant="body1Bold" sx={{ marginTop: tokens.aliasesSpaceComponentDefault }}>
                Scan configuration
              </Typography>
              <Typography variant="captionSubtle">
                Priority: ENV var → Account → Gateway → Code default
              </Typography>

              {SCAN_CONFIG_FIELDS.map(({ key, label, placeholder }) => (
                <Box key={key}>
                  <Typography variant="captionSubtle">{label}</Typography>
                  <Typography variant="body1">
                    {drawerGateway[key] ?? <span style={{ color: tokens.aliasesColorTextStaticSubtle }}>inherit (default: {placeholder})</span>}
                  </Typography>
                </Box>
              ))}

              <Box>
                <Typography variant="captionSubtle">Created</Typography>
                <Typography variant="body1">
                  {drawerGateway.created_at ? new Date(drawerGateway.created_at).toLocaleString() : '—'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="captionSubtle">Updated</Typography>
                <Typography variant="body1">
                  {drawerGateway.updated_at ? new Date(drawerGateway.updated_at).toLocaleString() : '—'}
                </Typography>
              </Box>

              {/* Backend Overrides Section */}
              <Typography variant="body1Bold" sx={{ marginTop: tokens.aliasesSpaceComponentDefault }}>
                Backend overrides
              </Typography>
              <Typography variant="captionSubtle">
                Regional backend routing for this gateway
              </Typography>

              {gatewayBackends.isLoading ? (
                <Skeleton variant="rectangular" height={80} />
              ) : gatewayBackends.items.length === 0 ? (
                <Typography variant="body1" sx={{ color: tokens.aliasesColorTextStaticSubtle }}>
                  No backend overrides configured
                </Typography>
              ) : (
                <List>
                  {gatewayBackends.items.map((config) => (
                    <ListItem
                      key={config.id}
                      bdsType="staticSubtle"
                      padding="default"
                    >
                      <Stack sx={{ width: '100%', gap: tokens.aliasesSpaceComponentDense }}>
                        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body1Bold">{config.backend_name}</Typography>
                          <Stack direction="row" sx={{ gap: tokens.aliasesSpaceComponentDense, alignItems: 'center' }}>
                            <Chip
                              label={config.enabled ? 'Enabled' : 'Disabled'}
                              bdsType={config.enabled ? 'staticSuccess' : 'staticNeutral'}
                              bdsSize="medium"
                            />
                            {isAdmin && (
                              <>
                                <IconButton
                                  bdsType="interactiveSubtle"
                                  onClick={() => openEditBackendDialog(config)}
                                >
                                  <Edit />
                                </IconButton>
                                <IconButton
                                  bdsType="interactiveSubtle"
                                  onClick={() => setDeleteBackendTarget(config)}
                                >
                                  <Delete />
                                </IconButton>
                              </>
                            )}
                          </Stack>
                        </Stack>
                        <Typography variant="captionSubtle" sx={{ fontFamily: 'monospace' }}>
                          {config.url}
                        </Typography>
                        <Typography variant="captionSubtle">
                          Timeout: {config.timeout}s
                        </Typography>
                      </Stack>
                    </ListItem>
                  ))}
                </List>
              )}

              <AADRoleGuard requires="ADMIN">
                {availableBackends.length > 0 && (
                  <Button
                    bdsType="interactiveNeutral"
                    startIcon={<Add />}
                    onClick={openCreateBackendDialog}
                  >
                    Add backend override
                  </Button>
                )}
              </AADRoleGuard>

              <AADRoleGuard requires="ADMIN">
                <Stack direction="row" sx={{ gap: tokens.aliasesSpaceComponentDefault, marginTop: tokens.aliasesSpaceComponentDefault }}>
                  <Button
                    bdsType="interactiveNeutral"
                    onClick={() => {
                      setDrawerGateway(null);
                      openEditDialog(drawerGateway);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    bdsType="interactiveSubtle"
                    onClick={() => {
                      setDrawerGateway(null);
                      setDeleteTarget(drawerGateway);
                    }}
                  >
                    Delete
                  </Button>
                </Stack>
              </AADRoleGuard>
            </Stack>
          </Box>
        )}
      </Drawer>

      {/* Backend config create/edit dialog */}
      <Dialog
        open={backendDialogOpen}
        onClose={closeBackendDialog}
        bdsDialogWidth="medium"
      >
        <DialogTitle
          dialogTitle={editingBackendConfig ? 'Edit backend override' : 'Add backend override'}
          showCloseIcon
          onClose={closeBackendDialog}
        />
        <DialogContent>
          <Stack sx={{ gap: tokens.aliasesSpaceComponentDefault }}>
            {!editingBackendConfig && (
              <Select
                label="Backend"
                value={backendForm.backend_id}
                onChange={(e) => setBackendForm({ ...backendForm, backend_id: e.target.value })}
                fullWidth
                bdsSize="small"
                error={Boolean(backendFormErrors.backend_id)}
                helperText={backendFormErrors.backend_id}
              >
                {availableBackends.map((b) => (
                  <MenuItem key={b.id} value={b.id.toString()}>
                    {b.name}
                  </MenuItem>
                ))}
              </Select>
            )}
            {editingBackendConfig && (
              <TextField
                label="Backend"
                value={editingBackendConfig.backend_name}
                disabled
                fullWidth
                bdsSize="small"
              />
            )}
            <TextField
              label="URL"
              value={backendForm.url}
              onChange={(e) => {
                setBackendForm({ ...backendForm, url: e.target.value });
                if (backendFormErrors.url) {
                  setBackendFormErrors((prev) => ({ ...prev, url: undefined }));
                }
              }}
              fullWidth
              bdsSize="small"
              placeholder="https://llm-guard.eu-west-1.example.com"
              error={Boolean(backendFormErrors.url)}
              helperText={backendFormErrors.url || 'Override URL for this backend'}
            />
            <TextField
              label="Timeout (seconds)"
              value={backendForm.timeout}
              onChange={(e) => {
                setBackendForm({ ...backendForm, timeout: e.target.value });
                if (backendFormErrors.timeout) {
                  setBackendFormErrors((prev) => ({ ...prev, timeout: undefined }));
                }
              }}
              fullWidth
              bdsSize="small"
              placeholder="30"
              error={Boolean(backendFormErrors.timeout)}
              helperText={backendFormErrors.timeout}
            />
            <TextField
              label="API Token"
              value={backendForm.api_token}
              onChange={(e) => setBackendForm({ ...backendForm, api_token: e.target.value })}
              fullWidth
              bdsSize="small"
              type="password"
              placeholder={editingBackendConfig ? '(unchanged)' : '(optional)'}
              helperText="Leave blank to keep existing token"
            />
            <Stack direction="row" sx={{ alignItems: 'center', gap: tokens.aliasesSpaceComponentDefault }}>
              <ToggleSwitch
                checked={backendForm.enabled}
                onChange={(e) => setBackendForm({ ...backendForm, enabled: e.target.checked })}
              />
              <Typography variant="body1">Enabled</Typography>
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={closeBackendDialog}>Cancel</Button>
          <Button bdsType="interactiveEmphasis" onClick={submitBackendForm}>
            {editingBackendConfig ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Backend config delete confirmation */}
      <Dialog
        open={!!deleteBackendTarget}
        onClose={() => setDeleteBackendTarget(null)}
        bdsDialogWidth="small"
      >
        <DialogTitle
          dialogTitle={`Delete ${deleteBackendTarget?.backend_name || 'backend override'}`}
          showCloseIcon
          onClose={() => setDeleteBackendTarget(null)}
        />
        <DialogContent
          content={
            deleteBackendTarget
              ? `This will remove the backend override for "${deleteBackendTarget.backend_name}". The gateway will use the default backend configuration.`
              : ''
          }
        />
        <DialogActions>
          <Button bdsType="interactiveNeutral" onClick={() => setDeleteBackendTarget(null)}>Cancel</Button>
          <Button bdsType="interactiveEmphasis" onClick={confirmDeleteBackend}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GatewaysContainer;
